import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { toast } from "sonner";
import { Flame } from "lucide-react";
import { api } from "@/lib/api";
import { useCart } from "@/context/CartContext";
import { ProductSkeleton } from "@/components/ProductCard";
import { BigCountdown, DealCard } from "@/components/deals";

export default function Deals() {
  const [items, setItems] = useState([]);
  const [loading, setLoading] = useState(true);
  const { addItem } = useCart();

  useEffect(() => {
    api
      .get("/products", { params: { deal: true, sort: "deal_ending", limit: 40 } })
      .then((r) => setItems(r.data.items))
      .catch(() => toast.error("Impossible de charger les offres du jour"))
      .finally(() => setLoading(false));
  }, []);

  const soonest = items[0]?.deal_ends_at;
  const add = (p) => {
    addItem(p, 1);
    toast.success(`${p.name} ajouté au panier`);
  };

  return (
    <div className="mx-auto max-w-7xl px-4 py-6 sm:px-6" data-testid="deals-page">
      <section className="relative overflow-hidden rounded-[28px] bg-gradient-to-br from-rose-700 via-rose-600 to-amber-600 p-6 text-white shadow-[0_28px_60px_-32px_rgba(190,18,60,0.7)] ds-rise sm:p-10">
        <div className="relative">
          <span className="ds-chip border-white/25 bg-white/15 text-white backdrop-blur">
            <Flame className="h-3.5 w-3.5" /> Offres du jour
          </span>
          <h1 className="mt-4 font-display text-3xl font-extrabold leading-[1.05] tracking-tight sm:text-4xl lg:text-5xl">
            Prix cassés,<br />
            <span className="text-amber-200">à durée limitée</span>
          </h1>
          <p className="mt-3 max-w-md text-sm leading-relaxed text-rose-50/90 sm:text-base">
            Une sélection d'articles à prix réduits. Chaque offre expire bientôt — profitez-en avant la fin du compte à rebours.
          </p>
          {soonest && (
            <div className="mt-6 inline-flex flex-col rounded-2xl bg-white/10 p-4 backdrop-blur-sm">
              <p className="mb-2 text-xs font-semibold uppercase tracking-wider text-rose-50">Prochaine offre expire dans</p>
              <BigCountdown target={soonest} />
            </div>
          )}
        </div>
      </section>

      {loading ? (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-5">
          {Array.from({ length: 8 }).map((_, i) => (
            <ProductSkeleton key={i} />
          ))}
        </div>
      ) : items.length === 0 ? (
        <div className="ds-card mt-6 p-10 text-center" data-testid="deals-empty">
          <Flame className="mx-auto h-10 w-10 text-rose-500" />
          <p className="mt-3 font-display text-lg font-bold text-slate-900">Aucune offre du jour en cours</p>
          <p className="mx-auto mt-2 max-w-md text-sm text-slate-600">
            Revenez bientôt : de nouvelles offres à durée limitée arrivent régulièrement. En attendant, découvrez tout le catalogue.
          </p>
          <Link to="/catalogue" className="ds-btn-primary mt-5" data-testid="deals-empty-catalog-cta">
            Voir le catalogue
          </Link>
        </div>
      ) : (
        <div className="mt-6 grid grid-cols-2 gap-3 sm:grid-cols-3 lg:grid-cols-4 sm:gap-5" data-testid="deals-grid">
          {items.map((p) => (
            <DealCard key={p.id} product={p} onAdd={add} />
          ))}
        </div>
      )}
    </div>
  );
}
